import * as Sentry from '@sentry/react';
import api from 'api';
import store from 'store';

import { makeFetchGate } from 'store/fetchRunsGate';

import { State } from 'store';
import IAsync from 'types/IAsync';
import IDashboardTaskCounts from 'types/IDashboardTaskCounts';

export interface IDashboardTaskCountsParams {
  projectIds?: string[];
  userId?: string;
  updatedWithinDays?: string;
}

function setTaskCountsAsync(taskCountsAsync: IAsync<IDashboardTaskCounts>) {
  store.setState((state: State) => ({
    dashboard: { ...state.dashboard, taskCountsAsync },
  }));
}

function buildUrl(params: IDashboardTaskCountsParams): string {
  const searchParams = new URLSearchParams();
  for (const projectId of params.projectIds ?? []) searchParams.append('project_id', projectId);
  if (params.userId) searchParams.set('user_id', params.userId);
  // Same window as the listing, or the chips count work the table cannot show.
  if (params.updatedWithinDays) searchParams.set('updated_within_days', params.updatedWithinDays);

  const qs = searchParams.toString();
  return `dashboard/task-counts${qs ? `?${qs}` : ''}`;
}

const taskCountsFetchGate = makeFetchGate();

export default function fetchDashboardTaskCounts(params: IDashboardTaskCountsParams = {}) {
  const url = buildUrl(params);
  const taskCountsAsync = store.getState().dashboard.taskCountsAsync;

  const begun = taskCountsFetchGate.begin(url, taskCountsAsync.status);
  if (begun.skip) return;

  // Counts for another project or user are not a stale view of these counts, so only an
  // unchanged query keeps its numbers on screen while the refetch runs.
  if (
    begun.nextStatus === 'REFRESH' &&
    (taskCountsAsync.status === 'SUCCESS' || taskCountsAsync.status === 'REFRESH')
  ) {
    setTaskCountsAsync({ status: 'REFRESH', data: taskCountsAsync.data });
  } else {
    setTaskCountsAsync({ status: 'PENDING' });
  }

  api
    .get(url)
    .json()
    .then(
      (data) => {
        if (!taskCountsFetchGate.isCurrent(begun.generation)) return;
        setTaskCountsAsync({
          status: 'SUCCESS',
          data: data as IDashboardTaskCounts,
        });
      },
      (error) => {
        if (!taskCountsFetchGate.isCurrent(begun.generation)) return;
        Sentry.captureException(error);
        setTaskCountsAsync({ status: 'ERROR' });
      },
    );
}
